import { Component } from "@/types"
import { Stack } from "@/ui/atoms/layout/Stack"
import { BodyText } from "@/ui/atoms/type/BodyText"
import { Title } from "@/ui/atoms/type/Title"
import { MarketingSection } from "@/ui/blocks/layout/MarketingSection"

import { cx } from "@/utils/cx"
import { SyllabusCard } from "./SyllabusCard"

export const COMPONENT_NAME = "courseSyllabus"

export const presets = {}

export interface Props extends Component {}

export function CourseSyllabus({ className, testId = COMPONENT_NAME }: Props) {
  return (
    <div className={cx(``, className)} data-test={testId}>
      <MarketingSection removeXPadding className="space-y-16">
        <MarketingSection removeYPadding>
          <Stack className="lg:mx-auto w-full max-w-[720px] gap-8 lg:text-center">
            <Title size="xl" className="lg:text-[32px]">
              What we cover
            </Title>
            <BodyText size="lg" className="lg:text-[20px] text-text-subtle font-light">
              Leading SANS instructors have carefully crafted a syllabus to cover the fundamentals
              needed for a successful career in cybersecurity.
            </BodyText>
          </Stack>
        </MarketingSection>
        <MarketingSection removeYPadding className="w-[100vw] md:w-auto overflow-x-auto">
          <div className="flex gap-6 md:grid md:grid-cols-3 lg:grid-cols-4">
            <SyllabusCard
              title="Computer Components & Concepts"
              image="/content/marketing/syllabuses/mod-cover-computers.png"
              items={[
                "Data Storage and Representation",
                "Logic and Data Manipulation",
                "Storing Data and Files",
                "Cloud Computing",
                "Operating Systems",
                "Virtualization",
              ]}
              className="text-purple-700 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Linux"
              image="/content/marketing/syllabuses/mod-cover-linux.png"
              items={[
                "The Linux Environment",
                "Linux Navigation",
                "Linux Commands",
                "Linux Architecture and Components",
              ]}
              className="text-yellow-300 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Networking Fundamentals and the Web"
              image="/content/marketing/syllabuses/mod-cover-networking.png"
              items={[
                "Networking",
                "Email",
                "DNS",
                "Packages & Protocols",
                "Search Superpowers",
                "WWW and Serving",
              ]}
              className="text-green-600 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Servers and Services"
              image="/content/marketing/syllabuses/mod-cover-servers.png"
              items={[
                "Database Servers",
                "DNS Servers",
                "Log Servers",
                "Email Servers",
                "Synchronisation Servers",
                "Cloud Computing",
              ]}
              className="text-gray-600 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Practical Programming and Concepts"
              image="/content/marketing/syllabuses/mod-cover-programming.png"
              items={[
                "Python - Data, Logic, Input and Output",
                "C - Data, Logic, Input and Output",
                "Programming Tips",
              ]}
              className="text-purple-500 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="SQL"
              image="/content/marketing/syllabuses/mod-cover-sql.png"
              items={[
                "MySQL Basic Statements",
                "MySQL Joins",
                "MySQL Operators",
                "MySQL Database Admin",
              ]}
              className="text-blue-400 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Windows Foundations"
              image="/content/marketing/syllabuses/mod-cover-windows.png"
              items={["Windows Permissions", "The Windows CLI", "Scripting Windows"]}
              className="text-green-500 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Advanced Computer Hardware"
              image="/content/marketing/syllabuses/mod-cover-hardware.png"
              items={["CPU & Memory", "Advanced Storage", "Containers"]}
              className="text-yellow-300 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Assembly"
              image="/content/marketing/syllabuses/mod-cover-assembly.png"
              items={["Exploring Mnemonics", "GDB in Practice"]}
              className="text-teal-600 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Security Concepts"
              image="/content/marketing/syllabuses/mod-cover-security.png"
              items={[
                "Encryption",
                "Law & Ethics",
                "Security Concerns",
                "Security Distributions",
                "Reconnaissance",
                "Core Forensics",
                "File Forensics",
                "Memory & Network Forensics",
              ]}
              className="text-red-400 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Offensive Security Concepts"
              image="/content/marketing/syllabuses/mod-cover-offensive.png"
              items={[
                "Binary Exploitation",
                "Metasploit & Social Engineering",
                "Linux Privilege Escalation",
                "Windows Privilege Escalation",
              ]}
              className="text-red-700 w-[320px] shrink-0 md:w-auto"
            />
            <SyllabusCard
              title="Network and Computer Infiltration"
              image="/content/marketing/syllabuses/mod-cover-infiltration.png"
              items={["Persistence", "Lateral Movement", "Exfiltration"]}
              className="text-orange-400 w-[320px] shrink-0 md:w-auto"
            />
            <div className="overflow-bookend md:hidden" />
          </div>
        </MarketingSection>
      </MarketingSection>
    </div>
  )
}
